import {
  BellRing,
  CreditCard,
  FileSearch,
  HandCoins,
  LayoutDashboard,
  Repeat,
  Settings,
  ShieldAlert,
  Users,
  UserCheck,
  Wallet,
  Landmark,
  PackageSearch,
  Route,
  BookCheck,
  ClipboardList,
  ShoppingCart,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { SessionUser } from '@shared/lib/auth/session-types'
import { hasAdminPermission } from '@shared/lib/auth/permissions'

type NavPath =
  | '/'
  | '/vendas'
  | '/recupera'
  | '/financas'
  | '/assinaturas'
  | '/produtos'
  | '/origens-checkout'
  | '/acao-necessaria'
  | '/alertas'
  | '/chargebacks'
  | '/reconciliacao'
  | '/contabilidade'
  | '/saques'
  | '/equipe'
  | '/usuarios'
  | '/config'
  | '/adquirentes'

export type NavCapability = 'recuperaAiEnabled'

export type NavItem = {
  to: NavPath
  label: string
  icon: LucideIcon
  permission?: string | readonly string[]
  hiddenWhenPermission?: string
  requiresCapability?: NavCapability
}

export const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, permission: 'REVENUE_READ' },
  { to: '/vendas', label: 'Vendas', icon: CreditCard, permission: 'SALE_READ' },
  { to: '/financas', label: 'Finanças', icon: Wallet, permission: 'RECEIVABLE_READ' },
  { to: '/assinaturas', label: 'Assinaturas', icon: Repeat, permission: 'SALE_READ' },
  {
    to: '/recupera',
    label: 'Recupera AI',
    icon: ShoppingCart,
    permission: 'REVENUE_READ',
    requiresCapability: 'recuperaAiEnabled',
  },
  { to: '/alertas', label: 'Alertas', icon: BellRing, permission: 'OPS_ALERT_READ' },
  { to: '/chargebacks', label: 'Chargebacks', icon: ShieldAlert, permission: 'CHARGEBACK_MANAGE' },
  { to: '/reconciliacao', label: 'Reconciliação', icon: FileSearch, permission: 'RECONCILE_READ' },
  { to: '/contabilidade', label: 'Contabilidade', icon: BookCheck, permission: 'RECONCILE_READ' },
  { to: '/saques', label: 'Recebimentos', icon: HandCoins, permission: 'PAYOUT_READ' },
  { to: '/usuarios', label: 'Usuários', icon: UserCheck, permission: 'USER_READ' },
  {
    to: '/acao-necessaria',
    label: 'Cadastros pendentes',
    icon: ClipboardList,
    permission: 'USER_READ',
  },
  {
    to: '/produtos',
    label: 'Produtos',
    icon: PackageSearch,
    permission: 'PRODUCT_READ',
    hiddenWhenPermission: 'USER_READ',
  },
  {
    to: '/origens-checkout',
    label: 'Origens do checkout',
    icon: Route,
    permission: 'PRODUCT_READ',
  },
  { to: '/adquirentes', label: 'Adquirentes', icon: Landmark, permission: 'ACQUIRER_READ' },
  { to: '/equipe', label: 'Equipe', icon: Users, permission: 'STAFF_MANAGE' },
  { to: '/config', label: 'Configurações', icon: Settings },
]

export const bottomNavItems: NavItem[] = navItems.filter((item) =>
  ['/', '/vendas', '/financas', '/assinaturas'].includes(item.to),
)

export const mobileMoreNavItems: NavItem[] = navItems.filter(
  (item) => !bottomNavItems.some((primaryItem) => primaryItem.to === item.to),
)

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  if (item.to === '/') return pathname === '/'
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

export function navItemCapabilityMet(
  item: NavItem,
  capabilities?: Partial<Record<NavCapability, boolean>>,
): boolean {
  if (!item.requiresCapability) {
    return true
  }
  return capabilities?.[item.requiresCapability] === true
}

export function canAccessNavItem(item: NavItem, user: SessionUser | null): boolean {
  if (item.hiddenWhenPermission && hasAdminPermission(user, item.hiddenWhenPermission)) {
    return false
  }
  if (!item.permission) {
    return true
  }
  const required = typeof item.permission === 'string' ? [item.permission] : item.permission
  return required.every((permission) => hasAdminPermission(user, permission))
}
